import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook for managing mobile navigation state and detection.
 * Provides mobile detection, nav open/close state, and auto-close on route change.
 *
 * @param {number} breakpoint - Pixel width below which is considered mobile (default: 768)
 * @returns {Object} Mobile navigation state and controls
 */
export function useMobileNav(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const location = useLocation();

  // Check if viewport is mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Initial check
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  // Close nav on route change
  useEffect(() => {
    setIsNavOpen(false);
  }, [location.pathname]);

  // Close nav when transitioning from mobile to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsNavOpen(false);
    }
  }, [isMobile]);

  const closeNav = useCallback(() => setIsNavOpen(false), []);
  const openNav = useCallback(() => setIsNavOpen(true), []);
  const toggleNav = useCallback(() => setIsNavOpen(prev => !prev), []);

  return {
    isMobile,
    isNavOpen,
    closeNav,
    openNav,
    toggleNav,
    setIsNavOpen,
  };
}

export default useMobileNav;
